'use client'

import { useEffect, useRef } from 'react'

import { DASHBOARD_EXPERIMENT_EXPOSURE_EVENT } from '@/components/ordersBidsDashboard/analyticsEvents'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

type DashboardExperimentVariant = 'treatment' | 'control'

/**
 * Fires the dashboard experiment exposure event once per page-load. The ref
 * guards against re-emission when `track` changes identity (it does when the
 * analytics interaction gate opens).
 *
 * If the experiment is kept beyond its evaluation window, don't copy this
 * file for the next one: generalize it into a shared emitter that takes the
 * event name and variant as props, and move it plus its event constant into
 * one module so each experiment's exposure concern lives (and can be deleted)
 * in a single place.
 */
export default function DashboardExperimentExposure({
  variant,
}: {
  variant: DashboardExperimentVariant
}) {
  const { track } = useAnalytics()

  const hasTrackedExposureRef = useRef(false)
  useEffect(() => {
    if (hasTrackedExposureRef.current) return
    hasTrackedExposureRef.current = true
    track(DASHBOARD_EXPERIMENT_EXPOSURE_EVENT, { variant })
  }, [track, variant])

  return null
}
